import axios from "axios";
import { apiUrl } from "../utils/backendUrl";

const LOG_ENDPOINT_PATH = "/api/v1/system-logs/client";
const LOG_ENDPOINT_URL = apiUrl(LOG_ENDPOINT_PATH);
const REQUEST_ID_HEADER = "x-request-id";

let isInitialized = false;
let flushTimer = null;
let flushInFlight = false;
let queue = [];

function nowIso() {
  return new Date().toISOString();
}

function truncate(value, max = 1200) {
  const text = `${value ?? ""}`;
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function getSessionId() {
  const key = "nihss_system_log_session_id";
  const existing = window.sessionStorage.getItem(key);
  if (existing) {
    return existing;
  }

  const created = `web_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
  window.sessionStorage.setItem(key, created);
  return created;
}

function absoluteUrl(url, baseURL) {
  if (!url) return window.location.href;
  try {
    return new URL(url, baseURL || window.location.origin).toString();
  } catch (_error) {
    return `${url}`;
  }
}

function shouldSkipUrl(url = "") {
  const normalized = `${url}`;
  return (
    normalized.includes(LOG_ENDPOINT_PATH) ||
    normalized.includes("/administrator/logs") ||
    normalized.includes("/api/v1/admin/system-logs")
  );
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flushSystemLogs();
  }, 1200);
}

function safeJson(value) {
  if (value == null) return value;
  if (typeof value === "string") return truncate(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.slice(0, 20).map((item) => safeJson(item));
  if (typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, entry]) => {
      if (key === "body" && /\/api\/admin\/config\/test-sms\/?(?:\?|$)/i.test(value.path || "")) {
        acc[key] = "[redacted SMS test request]";
        return acc;
      }
      const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, "");
      if (
        [
          "password",
          "passwordconfirm",
          "token",
          "authorization",
          "cookie",
          "setcookie",
        ].includes(normalized)
      ) {
        acc[key] = "[redacted]";
      } else {
        acc[key] = safeJson(entry);
      }
      return acc;
    }, {});
  }
  return truncate(value);
}

export function queueSystemLog(entry = {}) {
  queue.push({
    sourceApp: "web",
    createdAt: entry.createdAt || nowIso(),
    level: entry.level || "info",
    category: entry.category || "app",
    event: entry.event || "web.event",
    message: truncate(entry.message || "Web log"),
    tags: Array.isArray(entry.tags) ? entry.tags : [],
    details: safeJson(entry.details),
    request: safeJson(entry.request),
    actor: safeJson(entry.actor),
    device: {
      sessionId: getSessionId(),
      platform: navigator.platform || "",
      locale: navigator.language || "",
      model: navigator.userAgent || "",
      appVersion: process.env.REACT_APP_VERSION || "",
      buildNumber: process.env.REACT_APP_BUILD_NUMBER || "",
    },
    error: safeJson(entry.error),
    environment: {
      nodeEnv: process.env.NODE_ENV || "",
      releaseChannel: process.env.REACT_APP_ENV || "",
      gitSha: process.env.REACT_APP_GIT_SHA || "",
      service: "web-client",
    },
  });

  if (queue.length > 100) {
    queue = queue.slice(queue.length - 100);
  }

  scheduleFlush();
}

export async function flushSystemLogs({ useBeacon = false } = {}) {
  if (flushInFlight || queue.length === 0) {
    return;
  }

  const batch = queue.slice(0, 20);
  flushInFlight = true;

  try {
    if (useBeacon && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(
        LOG_ENDPOINT_URL,
        new Blob([JSON.stringify({ logs: batch })], {
          type: "application/json",
        })
      );
      if (ok) {
        queue = queue.slice(batch.length);
      }
      return;
    }

    const response = await fetch(LOG_ENDPOINT_URL, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ logs: batch }),
    });

    if (response.ok) {
      queue = queue.slice(batch.length);
    }
  } catch (_error) {
    // Best-effort only. Keep the queue so a later flush can retry.
  } finally {
    flushInFlight = false;
    if (queue.length > 0) {
      scheduleFlush();
    }
  }
}

export function logClientError(context, error, extra = {}) {
  queueSystemLog({
    level: "error",
    category: "error",
    event: context,
    message: error?.message || `${error || "Unknown browser error"}`,
    details: extra,
    error: {
      name: error?.name || "Error",
      message: error?.message || `${error || "Unknown browser error"}`,
      stack: error?.stack || "",
    },
  });
}

export function attachSystemLogInterceptors(instance) {
  if (!instance || instance.__nihssSystemLogsAttached) {
    return instance;
  }

  instance.__nihssSystemLogsAttached = true;

  instance.interceptors.request.use(
    (config) => {
      const url = absoluteUrl(config.url, config.baseURL);
      if (shouldSkipUrl(url)) {
        return config;
      }

      const requestId =
        config.headers?.[REQUEST_ID_HEADER] ||
        `webreq_${Date.now().toString(36)}_${Math.random()
          .toString(36)
          .slice(2, 8)}`;

      config.headers = config.headers || {};
      config.headers[REQUEST_ID_HEADER] = requestId;
      config.metadata = {
        ...(config.metadata || {}),
        systemLogStartedAt: performance.now(),
        systemLogRequestId: requestId,
        systemLogUrl: url,
      };

      queueSystemLog({
        level: "info",
        category: "http",
        event: "web.http.request",
        message: `${(config.method || "GET").toUpperCase()} ${url}`,
        request: {
          requestId,
          method: (config.method || "GET").toUpperCase(),
          path: url,
          body: safeJson(config.data),
          params: safeJson(config.params),
        },
      });

      return config;
    },
    (error) => {
      logClientError("web.http.request_error", error);
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      const meta = response.config?.metadata || {};
      const url = meta.systemLogUrl || absoluteUrl(response.config?.url, response.config?.baseURL);
      if (!shouldSkipUrl(url)) {
        queueSystemLog({
          level: "info",
          category: "http",
          event: "web.http.response",
          message: `${(response.config?.method || "GET").toUpperCase()} ${url} -> ${response.status}`,
          request: {
            requestId: meta.systemLogRequestId || "",
            method: (response.config?.method || "GET").toUpperCase(),
            path: url,
            statusCode: response.status,
            durationMs: Math.round(performance.now() - (meta.systemLogStartedAt || performance.now())),
          },
        });
      }
      return response;
    },
    (error) => {
      const config = error?.config || {};
      const meta = config.metadata || {};
      const url = meta.systemLogUrl || absoluteUrl(config.url, config.baseURL);
      if (!shouldSkipUrl(url)) {
        const statusCode = error?.response?.status || 0;
        queueSystemLog({
          level: statusCode >= 500 ? "error" : "warn",
          category: "http",
          event: "web.http.failure",
          message: `${(config.method || "GET").toUpperCase()} ${url} failed`,
          request: {
            requestId: meta.systemLogRequestId || "",
            method: (config.method || "GET").toUpperCase(),
            path: url,
            statusCode,
            durationMs: Math.round(
              performance.now() - (meta.systemLogStartedAt || performance.now())
            ),
          },
          error: {
            name: error?.name || "AxiosError",
            message: error?.message || "HTTP request failed",
            stack: error?.stack || "",
          },
          details: {
            response: safeJson(error?.response?.data),
          },
        });
      }
      return Promise.reject(error);
    }
  );

  return instance;
}

export function initSystemLogs() {
  if (isInitialized) return;
  isInitialized = true;

  attachSystemLogInterceptors(axios);

  window.addEventListener("error", (event) => {
    logClientError("window.error", event.error || new Error(event.message), {
      file: event.filename || "",
      line: event.lineno || 0,
      column: event.colno || 0,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    logClientError(
      "window.unhandledrejection",
      reason instanceof Error ? reason : new Error(`${reason || "Unhandled rejection"}`)
    );
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void flushSystemLogs({ useBeacon: true });
    }
  });
}
